import { useEffect, useState } from "react";
import {
  fetchAdminPlans,
  createAdminPlan,
  deleteAdminPlan,
} from "@/api/auth";

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    price: "",
    duration: "",
    is_popular: false,
    features: [""],
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("all"); // all, popular, active

  // تحميل الخطط
  const loadPlans = async () => {
    try {
      const data = await fetchAdminPlans();
      setPlans(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  // تصفية الخطط حسب التبويب النشط
  const filteredPlans = plans.filter(plan => {
    if (activeTab === "popular") return plan.is_popular;
    if (activeTab === "active") return plan.is_active;
    return true;
  });

  // إضافة/تعديل خطة
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const planData = {
        title: form.title.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        duration: Number(form.duration),
        is_popular: form.is_popular,
        features: form.features.filter(f => f.trim() !== ""),
      };

      if (editingId) {
        // تعديل خطة موجودة
        // await updateAdminPlan(editingId, planData);
      } else {
        // إضافة خطة جديدة
        await createAdminPlan(planData);
      }

      setForm({
        title: "",
        description: "",
        price: "",
        duration: "",
        is_popular: false,
        features: [""],
      });
      setEditingId(null);
      loadPlans();
    } catch (e) {
      setError("فشل حفظ الخطة");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // حذف خطة
  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه الخطة؟")) return;

    try {
      await deleteAdminPlan(id);
      loadPlans();
    } catch (e) {
      console.error(e);
      setError("فشل حذف الخطة");
    }
  };

  // تحرير خطة
  const handleEdit = (plan) => {
    setForm({
      title: plan.title,
      description: plan.description,
      price: plan.price,
      duration: plan.duration,
      is_popular: plan.is_popular || false,
      features: plan.features?.map(f => f.feature || f) || [""],
    });
    setEditingId(plan.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // إضافة ميزة جديدة
  const addFeature = () => {
    setForm({ ...form, features: [...form.features, ""] });
  };

  // تحديث ميزة
  const updateFeature = (index, value) => {
    const features = [...form.features];
    features[index] = value;
    setForm({ ...form, features });
  };

  // حذف ميزة
  const removeFeature = (index) => {
    const features = form.features.filter((_, i) => i !== index);
    setForm({ ...form, features: features.length > 0 ? features : [""] });
  };

  return (
    <div className="plans-container">
      <div className="plans-header">
        <h1 className="plans-title">إدارة الخطط والاشتراكات</h1>
        <p className="plans-subtitle">أنشئ وعُدّل خطط الاشتراك للمستخدمين</p>
      </div>

      {/* نموذج الإضافة/التعديل */}
      <div className="plans-form-container">
        <h2 className="form-title">
          {editingId ? "تعديل الخطة" : "إضافة خطة جديدة"}
        </h2>
        
        <form onSubmit={handleSubmit} className="plans-form">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                اسم الخطة *
                <span className="label-hint">مثال: الخطة الأساسية، الخطة المميزة</span>
              </label>
              <input
                className="form-input"
                placeholder="أدخل اسم الخطة"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                السعر *
                <span className="label-hint">بالدولار الأمريكي (USD)</span>
              </label>
              <div className="price-input">
                <input
                  className="form-input"
                  type="number"
                  placeholder="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                />
                <span className="currency">$</span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                المدة *
                <span className="label-hint">عدد الأيام</span>
              </label>
              <div className="duration-input">
                <input
                  className="form-input"
                  type="number"
                  placeholder="30"
                  value={form.duration}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  required
                  min="1"
                />
                <span className="duration-unit">يوم</span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                الوصف
                <span className="label-hint">وصف مختصر للخطة</span>
              </label>
              <textarea
                className="form-textarea"
                placeholder="وصف الخطة..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={form.is_popular}
                  onChange={(e) => setForm({ ...form, is_popular: e.target.checked })}
                />
                <span className="checkbox-text">علامة "الأكثر شعبية"</span>
                <span className="checkbox-hint">عرض هذه الخطة كمميزة وشائعة</span>
              </label>
            </div>
          </div>

          {/* قائمة المميزات */}
          <div className="features-section">
            <div className="section-header">
              <h3 className="section-title">مميزات الخطة</h3>
              <p className="section-subtitle">أضف ميزات الخطة واحدة تلو الأخرى</p>
            </div>
            
            <div className="features-list">
              {form.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <div className="feature-input-wrapper">
                    <span className="feature-number">{index + 1}</span>
                    <input
                      className="feature-input"
                      placeholder={`أدخل الميزة ${index + 1}`}
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                    />
                    {form.features.length > 1 && (
                      <button
                        type="button"
                        className="remove-feature-btn"
                        onClick={() => removeFeature(index)}
                        aria-label="حذف الميزة"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="add-feature-btn"
              onClick={addFeature}
            >
              <span className="add-icon">+</span>
              إضافة ميزة جديدة
            </button>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  جاري الحفظ...
                </>
              ) : editingId ? "حفظ التعديلات" : "إضافة الخطة"}
            </button>
            
            {editingId && (
              <button 
                type="button"
                className="cancel-btn"
                onClick={() => {
                  setForm({ 
                    title: "", 
                    description: "", 
                    price: "", 
                    duration: "", 
                    is_popular: false, 
                    features: [""] 
                  });
                  setEditingId(null);
                }}
              >
                إلغاء التعديل
              </button>
            )}
          </div>

          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
        </form>
      </div>

      {/* قائمة الخطط */}
      <div className="plans-list-container">
        <div className="list-header">
          <div className="tabs">
            <button 
              className={`tab ${activeTab === "all" ? "active" : ""}`}
              onClick={() => setActiveTab("all")}
            >
              جميع الخطط ({plans.length})
            </button>
            <button 
              className={`tab ${activeTab === "popular" ? "active" : ""}`}
              onClick={() => setActiveTab("popular")}
            >
              الأكثر شعبية ({plans.filter(p => p.is_popular).length})
            </button>
            <button 
              className={`tab ${activeTab === "active" ? "active" : ""}`}
              onClick={() => setActiveTab("active")}
            >
              النشطة ({plans.filter(p => p.is_active).length})
            </button>
          </div>
        </div>

        {filteredPlans.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💎</div>
            <h3 className="empty-title">لا توجد خطط</h3>
            <p className="empty-subtitle">
              {activeTab === "all" 
                ? "ابدأ بإضافة أول خطة" 
                : `لا توجد خطط ${activeTab === "popular" ? "شعبية" : "نشطة"}`
              }
            </p>
          </div>
        ) : (
          <div className="plans-grid">
            {filteredPlans.map((plan) => (
              <div 
                key={plan.id} 
                className={`plan-card ${plan.is_popular ? "popular" : ""} ${editingId === plan.id ? "editing" : ""}`}
              >
                {plan.is_popular && (
                  <div className="popular-badge">
                    <span className="badge-icon">🔥</span>
                    الأكثر شعبية
                  </div>
                )}

                <div className="plan-header">
                  <h3 className="plan-title">{plan.title}</h3>
                  <div className="plan-price">
                    <span className="price-amount">${plan.price}</span>
                    <span className="price-period">/ {plan.duration} يوم</span>
                  </div>
                </div>

                {plan.description && (
                  <p className="plan-description">{plan.description}</p>
                )}

                <div className="plan-features">
                  <h4 className="features-title">المميزات:</h4>
                  <ul className="features-list">
                    {plan.features?.map((feature, index) => (
                      <li key={index} className="feature-item">
                        <span className="feature-icon">✓</span>
                        <span>{feature.feature || feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="plan-footer">
                  <div className="plan-meta">
                    <span className="meta-item">
                      <span className="meta-icon">🆔</span>
                      ID: {plan.id}
                    </span>
                    <span className="meta-item">
                      <span className="meta-icon">📅</span>
                      {plan.created_at ? new Date(plan.created_at).toLocaleDateString('ar-SA') : "غير محدد"}
                    </span>
                  </div>

                  <div className="plan-actions">
                    <button 
                      className="action-btn edit-btn"
                      onClick={() => handleEdit(plan)}
                    >
                      <span className="action-icon">✏️</span>
                      تعديل
                    </button>
                    <button 
                      className="action-btn delete-btn"
                      onClick={() => handleDelete(plan.id)}
                    >
                      <span className="action-icon">🗑️</span>
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .plans-container {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .plans-header {
          text-align: center;
          margin-bottom: 3rem;
          padding-bottom: 2rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .plans-title {
          color: #1e293b;
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
          background: linear-gradient(135deg, #059669, #10b981);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .plans-subtitle {
          color: #64748b;
          font-size: 1.1rem;
          max-width: 600px;
          margin: 0 auto;
        }

        .plans-form-container {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 3rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
        }

        .form-title {
          color: #1e293b;
          font-size: 1.5rem;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #10b981;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .form-group:nth-child(4),
        .form-group:nth-child(5) {
          grid-column: 1 / -1;
        }

        .form-label {
          color: #334155;
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .label-hint {
          color: #64748b;
          font-weight: normal;
          font-size: 0.875rem;
        }

        .form-input {
          padding: 0.875rem 1rem;
          border: 2px solid #cbd5e1;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: #f8fafc;
          width: 100%;
        }

        .form-input:focus {
          outline: none;
          border-color: #10b981;
          background: white;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .price-input,
        .duration-input {
          position: relative;
        }

        .currency,
        .duration-unit {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #64748b;
          font-weight: 500;
        }

        .price-input .form-input,
        .duration-input .form-input {
          padding-left: 2.5rem;
        }

        .form-textarea {
          padding: 1rem;
          border: 2px solid #cbd5e1;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: #f8fafc;
          resize: vertical;
          font-family: inherit;
          width: 100%;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #10b981;
          background: white;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .checkbox-group {
          padding: 1rem;
          background: #f1f5f9;
          border-radius: 10px;
        }

        .checkbox-label {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          cursor: pointer;
        }

        .checkbox-input {
          width: 18px;
          height: 18px;
          accent-color: #10b981;
          margin-left: 0.5rem;
        }

        .checkbox-text {
          font-weight: 600;
          color: #334155;
        }

        .checkbox-hint {
          color: #64748b;
          font-size: 0.875rem;
        }

        .features-section {
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: #f8fafc;
          border-radius: 12px;
        }

        .section-header {
          margin-bottom: 1.5rem;
        }

        .section-title {
          color: #334155;
          font-size: 1.2rem;
          margin-bottom: 0.25rem;
        }

        .section-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }

        .features-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .feature-input-wrapper {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          width: 100%;
        }

        .feature-number {
          background: #10b981;
          color: white;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
          font-weight: 600;
          flex-shrink: 0;
        }

        .feature-input {
          flex: 1;
          padding: 0.75rem 1rem;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }

        .feature-input:focus {
          outline: none;
          border-color: #10b981;
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
        }

        .remove-feature-btn {
          background: #fecaca;
          color: #dc2626;
          border: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 1.2rem;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .remove-feature-btn:hover {
          background: #fca5a5;
        }

        .add-feature-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #d1fae5;
          color: #059669;
          border: 2px dashed #10b981;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          width: 100%;
          justify-content: center;
        }

        .add-feature-btn:hover {
          background: #a7f3d0;
        }

        .add-icon {
          font-size: 1.2rem;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .submit-btn {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          border: none;
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 160px;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .cancel-btn {
          background: #f1f5f9;
          color: #64748b;
          border: 2px solid #cbd5e1;
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-btn:hover {
          background: #e2e8f0;
          color: #475569;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .error-alert {
          background: #fef2f2;
          color: #dc2626;
          padding: 1rem;
          border-radius: 10px;
          border: 1px solid #fecaca;
          margin-top: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .error-icon {
          font-size: 1.2rem;
        }

        .plans-list-container {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
        }

        .list-header {
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .tabs {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .tab {
          padding: 0.75rem 1.5rem;
          background: #f1f5f9;
          border: none;
          border-radius: 8px;
          color: #64748b;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .tab:hover {
          background: #e2e8f0;
          color: #475569;
        }

        .tab.active {
          background: #10b981;
          color: white;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          opacity: 0.3;
        }

        .empty-title {
          color: #475569;
          margin-bottom: 0.5rem;
        }

        .empty-subtitle {
          color: #94a3b8;
        }

        .plans-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .plan-card {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 1.5rem;
          position: relative;
          transition: all 0.3s ease;
        }

        .plan-card:hover {
          border-color: #cbd5e1;
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .plan-card.popular {
          border-color: #10b981;
          background: linear-gradient(to bottom, white, #f0fdf4);
        }

        .plan-card.editing {
          border-color: #3b82f6;
          background: #eff6ff;
        }

        .popular-badge {
          position: absolute;
          top: -12px;
          left: 1.5rem;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
          padding: 0.25rem 1rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .badge-icon {
          font-size: 0.875rem;
        }

        .plan-header {
          margin-bottom: 1rem;
        }

        .plan-title {
          color: #1e293b;
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .plan-price {
          display: flex;
          align-items: baseline;
          gap: 0.25rem;
        }

        .price-amount {
          color: #059669;
          font-size: 2rem;
          font-weight: 700;
        }

        .price-period {
          color: #64748b;
          font-size: 0.875rem;
        }

        .plan-description {
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .plan-features {
          margin-bottom: 1.5rem;
        }

        .features-title {
          color: #334155;
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
        }

        .features-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .feature-item {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
          color: #475569;
          font-size: 0.875rem;
        }

        .feature-icon {
          color: #10b981;
          font-weight: bold;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .plan-footer {
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .plan-meta {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1rem;
          font-size: 0.75rem;
          color: #94a3b8;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .meta-icon {
          font-size: 0.875rem;
        }

        .plan-actions {
          display: flex;
          gap: 0.75rem;
        }

        .action-btn {
          flex: 1;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          justify-content: center;
          border: none;
        }

        .edit-btn {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .edit-btn:hover {
          background: #bfdbfe;
        }

        .delete-btn {
          background: #fee2e2;
          color: #dc2626;
        }

        .delete-btn:hover {
          background: #fecaca;
        }

        .action-icon {
          font-size: 1rem;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .plans-container {
            padding: 1rem;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .plans-grid {
            grid-template-columns: 1fr;
          }

          .tabs {
            flex-direction: column;
          }

          .tab {
            width: 100%;
            text-align: center;
          }

          .form-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .submit-btn, .cancel-btn {
            width: 100%;
            justify-content: center;
          }

          .plan-card {
            padding: 1.25rem;
          }

          .plan-meta {
            flex-direction: column;
            gap: 0.5rem;
          }
        }

        @media (max-width: 480px) {
          .plans-title {
            font-size: 2rem;
          }

          .plans-form-container,
          .plans-list-container {
            padding: 1.5rem;
          }

          .feature-input-wrapper {
            flex-direction: column;
            align-items: flex-start;
          }

          .feature-input {
            width: 100%;
          }

          .plan-actions {
            flex-direction: column;
          }

          .action-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}